import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import TWLogo from '../../assets/TW-Rental.svg';
import PrivacyPopup from '../../components/PrivacyPopup/PrivacyPopup';
import Toast from '../../components/Toast/Toast';
import './Contact.css';

const Contact = () => {
  const [isPrivacyPopupOpen, setIsPrivacyPopupOpen] = useState(false);
  const [isConsentAccepted, setIsConsentAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'success' });
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleCheckboxClick = () => {
    if (!isConsentAccepted) {
      setIsPrivacyPopupOpen(true);
    } else {
      setIsConsentAccepted(false);
    }
  };

  const handleAcceptPrivacy = () => {
    setIsConsentAccepted(true);
    setIsPrivacyPopupOpen(false);
  };

  const handleClosePrivacy = () => {
    setIsPrivacyPopupOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const showToast = (message, type = 'success') => {
    setToast({ isVisible: true, message, type });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!isConsentAccepted) {
      showToast('Please accept the privacy terms to continue.', 'error');
      return;
    }

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.message) {
      showToast('Please fill in all required fields.', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // Make HTTP call to backend
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          consentAccepted: isConsentAccepted,
          timestamp: new Date().toISOString()
        })
      });
      
      // if (!response.ok) {
      //   throw new Error(`HTTP error! status: ${response.status}`);
      // }
      console.log('Response:', response);

      // Store submission for Customer Support
      const submissions = JSON.parse(localStorage.getItem('contactFormSubmissions') || '[]');
      submissions.push({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        timestamp: new Date().toISOString()
      });
      localStorage.setItem('contactFormSubmissions', JSON.stringify(submissions));

      // Show success toast
      showToast('Form has been submitted to the backend for further processing!', 'success');

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
      });
      setIsConsentAccepted(false);

    } catch (error) {
      console.error('Error submitting form:', error);
      showToast('There was an error submitting your form. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Header Section */}
      <div className="contact-header">
        <div className="logo">
          <img src={TWLogo} alt="TW Rental Logo" className="logo-svg" />
        </div>
        
        <div className="navigation">
          <Link to="/home" className="nav-button">Home</Link>
          <Link to="/about" className="nav-button">About Us</Link>
          <button className="nav-button active">Contact Us</button>
        </div>
      </div>
      
      {/* Main Content Section */}
      <div className="contact-content">
        <div className="contact-form-container">
          <div className="contact-title-banner">
            <h1>CONTACT US</h1>
          </div>
          
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <input 
                type="text" 
                name="firstName"
                placeholder="First name*" 
                value={formData.firstName}
                onChange={handleInputChange}
                required 
              />
              <input 
                type="text" 
                name="lastName"
                placeholder="Last name*" 
                value={formData.lastName}
                onChange={handleInputChange}
                required 
              />
            </div>
            
            <input 
              type="email" 
              name="email"
              placeholder="Email address*" 
              value={formData.email}
              onChange={handleInputChange}
              required 
            />
            <input 
              type="tel" 
              name="phone"
              placeholder="Phone number*" 
              value={formData.phone}
              onChange={handleInputChange}
              required 
            />
            <textarea 
              name="message"
              placeholder="Message*" 
              rows="5" 
              value={formData.message}
              onChange={handleInputChange}
              required
            ></textarea>
            
            <div className="consent-section">
              <label className="consent-checkbox">
                <input 
                  type="checkbox" 
                  checked={isConsentAccepted}
                  onChange={handleCheckboxClick}
                  required 
                />
                <span className="checkmark"></span>
                <span className="consent-text">By providing your phone number and submitting this form, you agree to receive automated text messages from <strong>TW Rental</strong> related to your rental reservation — including booking confirmations, pickup instructions, support updates, and safety reminders. Message frequency varies. Message and data rates may apply. Reply STOP to cancel or HELP for help. Read our <br />
                <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</span>
              </label>
            </div>
            
            <button 
              type="submit" 
              className="send-button" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send'}
            </button>
          </form>
        </div>
      </div>
      
      {/* Footer Section */}
      <div className="contact-footer">
        <div className="copyright">© 2024. All Rights Reserved.</div>
        <div className="social-icons">
          <span className="social-icon">f</span>
          <span className="social-icon">📷</span>
          <span className="social-icon">in</span>
        </div>
      </div>
      
      {/* Privacy Popup */}
      <PrivacyPopup 
        isOpen={isPrivacyPopupOpen}
        onClose={handleClosePrivacy}
        onAccept={handleAcceptPrivacy}
      />
      
      {/* Toast Notification */}
      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </div>
  );
};

export default Contact;
